import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import HomePage from './screens/HomePage';
import LoginPage from './screens/LoginPage';
import ChatPage from './screens/ChatPage';
import VoiceChatPage from './screens/VoiceChatPage';
import ProfilePage from './screens/ProfilePage';
import SuperAdminConfigPage from './screens/SuperAdminConfigPage';
import PromptManagementPage from './screens/PromptManagementPage';
import KnowledgeBankPage from './screens/KnowledgeBankPage';
import UserManagementPage from './screens/UserManagementPage';
import AnalyticsPage from './screens/AnalyticsPage';
import LoanManagementPage from './screens/LoanManagementPage';
import { UserProvider } from './contexts/UserContext';
import RegisterPage from './screens/RegisterPage';
import SubscriptionPlansScreen from './screens/SubscriptionPlansScreen';
import SubscribeScreen from './screens/SubscribeScreen';
import UsageDashboard from './screens/UsageDashboard';
import ManageAdminSubscriptionsScreen from './screens/ManageAdminSubscriptionsScreen';
import SystemAdminDashboard from './screens/system_admin/SystemAdminDashboard';
import PropertiesPage from './screens/PropertiesPage';
import ContactsPage from './screens/ContactsPage';
import OperationsPage from './screens/OperationsPage';
import CoreSettingsPage from './screens/CoreSettingsPage';
import ForgotPasswordPage from './screens/ForgotPasswordPage';
import ResetPasswordPage from './screens/ResetPasswordPage';

import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import ComplianceDashboard from './screens/compliance/ComplianceDashboard';
import ReportsPage from './screens/compliance/Reports/ReportsPage';
import SettingsPage from './screens/compliance/Settings/SettingsPage';

const httpLink = createHttpLink({
  uri: '/api/graphql/',
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Token ${token}` : "",
    }
  }
});

const apolloClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache()
});

// Create a custom theme with the new Omni Property palette
const theme = createTheme({
  palette: {
    primary: {
      main: '#1B4A4D', // Deep Teal
      light: '#2A7276',
      dark: '#113032',
      contrastText: '#FAF7F3', // Warm Ivory
    },
    secondary: {
      main: '#E8DDD0', // Warm Sand
      light: '#F5F0EA',
      dark: '#C8BBAA',
      contrastText: '#1B4A4D', // Deep Teal
    },
    error: {
      main: '#C2544A', // Muted Red
    },
    warning: {
      main: '#D4953A', // Warm Amber
    },
    success: {
      main: '#5B8A72', // Sage Green
    },
    background: {
      default: '#FAF7F3', // Warm Ivory
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C2C2C', // Charcoal
      secondary: '#6B6360', // Muted Brown-Grey
    },
    divider: '#EDE5DB', // Soft Sand
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

// Role-based Route Component
const RoleBasedRoute = ({ allowedRoles, children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

function App() {
  return (
    <ApolloProvider client={apolloClient}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ChatProvider>
          <UserProvider>
            <SubscriptionProvider>
              <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<HomePage />} />
                  <Route path="chat" element={<ChatPage />} />
                  <Route path="voice-chat" element={<VoiceChatPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="properties" element={<PropertiesPage />} />
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="operations" element={<OperationsPage />} />
                  <Route path="compliance/dashboard" element={<ComplianceDashboard />} />
                  <Route path="compliance/reports" element={<ReportsPage />} />
                  <Route path="compliance/settings" element={<SettingsPage />} />
                  <Route path="core-settings" element={
                    <RoleBasedRoute allowedRoles={['system_admin', 'tpb_manager']}>
                      <CoreSettingsPage />
                    </RoleBasedRoute>
                  } />

                  {/* Admin Routes */}
                  <Route path="admin/config" element={
                    <RoleBasedRoute allowedRoles={['system_admin']}>
                      <SuperAdminConfigPage />
                    </RoleBasedRoute>
                  } />
                  <Route path="admin/prompts" element={
                    <RoleBasedRoute allowedRoles={['system_admin']}>
                      <PromptManagementPage />
                    </RoleBasedRoute>
                  } />
                  <Route path="admin/knowledge" element={
                    <RoleBasedRoute allowedRoles={['system_admin']}>
                      <KnowledgeBankPage />
                    </RoleBasedRoute>
                  } />
                  <Route path="admin/users" element={
                    <RoleBasedRoute allowedRoles={['system_admin', 'tpb_manager']}>
                      <UserManagementPage />
                    </RoleBasedRoute>
                  } />
                  <Route path="admin/analytics" element={
                    <RoleBasedRoute allowedRoles={['system_admin', 'tpb_manager']}>
                      <AnalyticsPage />
                    </RoleBasedRoute>
                  } />
                  <Route path="admin/loans" element={
                    <RoleBasedRoute allowedRoles={['system_admin', 'tpb_manager', 'tpb_staff']}>
                      <LoanManagementPage />
                    </RoleBasedRoute>
                  } />

                  {/* Subscription Routes */}
                  <Route path="subscription-plans" element={
                    <RoleBasedRoute allowedRoles={['system_admin', 'tpb_manager']}>
                      <SubscriptionPlansScreen />
                    </RoleBasedRoute>
                  } />
                  <Route path="manage-admin-subscriptions" element={
                    <RoleBasedRoute allowedRoles={['system_admin']}>
                      <ManageAdminSubscriptionsScreen />
                    </RoleBasedRoute>
                  } />
                  <Route path="subscribe" element={
                    <RoleBasedRoute allowedRoles={['tpb_manager']}>
                      <SubscribeScreen />
                    </RoleBasedRoute>
                  } />
                  <Route path="usage" element={<UsageDashboard />} />
                  <Route path="admin-dashboard" element={
                    <RoleBasedRoute allowedRoles={['system_admin']}>
                      <SystemAdminDashboard />
                    </RoleBasedRoute>
                  } />
                </Route>

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Router>
            </SubscriptionProvider>
          </UserProvider>
        </ChatProvider>
      </AuthProvider>
    </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;